import { useState, useEffect, useCallback } from 'react';
import FingerprintJS, { Agent, GetResult } from '@fingerprintjs/fingerprintjs';

// Singleton for the fingerprint agent
let fpPromise: Promise<Agent> | null = null;

const getFingerprint = async (): Promise<GetResult> => {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load();
  }
  const fp = await fpPromise;
  return fp.get();
};

interface FingerprintState {
  visitorId: string | null;
  loading: boolean;
  error: Error | null;
}

interface UseFingerprintReturn extends FingerprintState {
  refresh: () => Promise<string | null>;
}

/**
 * Hook to get browser fingerprint for anonymous user tracking.
 * Uses FingerprintJS open-source library to generate a stable visitor ID
 * based on browser characteristics (screen, fonts, canvas, etc.)
 * 
 * The visitor ID persists across:
 * - Page reloads
 * - IP changes (VPN)
 * - Most incognito sessions
 * 
 * @returns {UseFingerprintReturn} Object containing visitorId, loading state, error, and refresh function
 */
export function useFingerprint(): UseFingerprintReturn {
  const [state, setState] = useState<FingerprintState>({
    visitorId: null,
    loading: true,
    error: null,
  });

  const fetchFingerprint = useCallback(async (): Promise<string | null> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const result = await getFingerprint();
      // Log fingerprint to console for debugging/admin purposes
      console.log('[Fingerprint] Your browser fingerprint ID:', result.visitorId);
      setState({
        visitorId: result.visitorId,
        loading: false,
        error: null,
      });
      return result.visitorId;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get fingerprint');
      setState({
        visitorId: null,
        loading: false,
        error,
      });
      return null;
    }
  }, []);

  useEffect(() => {
    fetchFingerprint();
  }, [fetchFingerprint]);

  return {
    ...state,
    refresh: fetchFingerprint,
  };
}

export default useFingerprint;

